import React, { useState, useEffect } from 'react';
import { lookupsApi, backtestApi } from '../../services/api';
import { Strategy, Instrument, Resolution } from '../../types';
import Dropdown from '../../components/common/Dropdown';
import Button from '../../components/common/Button';
import ResultModal from '../../components/common/ResultModal';
import './RunBacktest.css';

const RunBacktest: React.FC = () => {
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [instruments, setInstruments] = useState<Instrument[]>([]);
  const [resolutions, setResolutions] = useState<Resolution[]>([]);

  const [selectedStrategy, setSelectedStrategy] = useState('');
  const [selectedInstrument, setSelectedInstrument] = useState('');
  const [selectedResolution, setSelectedResolution] = useState('');
  const [fromDate, setFromDate] = useState('2024-01-01');
  const [toDate, setToDate] = useState('2025-01-01');

  // Position size for backtests
  const [positionSize, setPositionSize] = useState(500);

  const [loading, setLoading] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalVariant, setModalVariant] = useState<'success' | 'error' | 'warning'>('success');
  const [modalTitle, setModalTitle] = useState('');
  const [modalContent, setModalContent] = useState<any>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [strats, insts, ress] = await Promise.all([
        lookupsApi.getStrategies(),
        lookupsApi.getInstruments(),
        lookupsApi.getResolutions()
      ]);
      setStrategies(strats);
      setInstruments(insts);
      setResolutions(ress);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleRunBacktest = async () => {
    if (!selectedStrategy || !selectedInstrument || !selectedResolution) {
      alert('Please select strategy, instrument, and resolution');
      return;
    }

    setLoading(true);
    try {
      const requestPayload = {
        userId: 1,
        strategyId: Number(selectedStrategy),
        epic: selectedInstrument,
        resolution: selectedResolution,
        fromDate: `${fromDate}T00:00:00`,
        toDate: `${toDate}T00:00:00`,
        positionSize: positionSize
      };

      const result = await backtestApi.runBacktest(requestPayload);

      console.log('Backtest result:', result);

      // Extract values with fallback for both naming conventions
      const totalTrades = result.totalTrades || result.total_trades || 0;
      const winningTrades = result.winningTrades || result.winning_trades || 0;
      const losingTrades = result.losingTrades || result.losing_trades || 0;
      const winRate = result.winRate || result.win_rate || 0;
      const totalProfitLoss = result.totalProfitLoss || result.total_profit_loss || 0;
      const mlStatus = result.mlStatus;
      const mlWarning = result.mlWarning;

      // Prepare modal content
      const content = (
        <div className="backtest-summary">
          <div className="summary-cards-grid">
            <div className="summary-card">
              <span className="summary-label">Total Trades</span>
              <span className="summary-value">{totalTrades}</span>
            </div>
            <div className="summary-card">
              <span className="summary-label">Winning Trades</span>
              <span className="summary-value success-text">{winningTrades}</span>
            </div>
            <div className="summary-card">
              <span className="summary-label">Losing Trades</span>
              <span className="summary-value error-text">{losingTrades}</span>
            </div>
            <div className="summary-card">
              <span className="summary-label">Win Rate</span>
              <span className="summary-value">{winRate.toFixed(2)}%</span>
            </div>
            <div className="summary-card">
              <span className="summary-label">Total P/L</span>
              <span className={`summary-value ${totalProfitLoss >= 0 ? 'success-text' : 'error-text'}`}>
                ${totalProfitLoss.toFixed(2)}
              </span>
            </div>
          </div>
          {mlStatus && (
            <div className="ml-status-section">
              <div className="ml-status-card">
                <span className="summary-label">ML Status</span>
                <span className="summary-value">{mlStatus}</span>
              </div>
            </div>
          )}
          {mlWarning && (
            <div className="ml-warning-box">
              <strong>ML Warning:</strong> {mlWarning}
            </div>
          )}
        </div>
      );

      setModalTitle('Backtest Completed Successfully');
      setModalVariant('success');
      setModalContent(content);
      setShowModal(true);
    } catch (error: any) {
      console.error('Error running backtest:', error);

      // Extract error message from axios error response
      let errorMessage = 'Failed to run backtest.';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data) {
        errorMessage = typeof error.response.data === 'string'
          ? error.response.data
          : 'Failed to run backtest. Check console for details.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setModalTitle('Backtest Failed');
      setModalVariant('error');
      setModalContent(<div className="error-message">{errorMessage}</div>);
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="run-backtest-page">
      <div className="page-header">
        <h2>Run New Backtest</h2>
        <p className="page-description">Configure and execute a new backtest with your chosen strategy and parameters</p>
      </div>

      <div className="backtest-config-card">
        <div className="form-grid">
          <Dropdown
            label="Strategy"
            value={selectedStrategy}
            onChange={setSelectedStrategy}
            options={strategies.map(s => ({
              value: s.strategy_id,
              label: s.strategy_name
            }))}
            placeholder="Select Strategy"
          />

          <Dropdown
            label="Instrument"
            value={selectedInstrument}
            onChange={setSelectedInstrument}
            options={instruments.map(i => ({
              value: i.epic,
              label: `${i.name} (${i.epic})`
            }))}
            placeholder="Select Instrument"
          />

          <Dropdown
            label="Resolution"
            value={selectedResolution}
            onChange={setSelectedResolution}
            options={resolutions.map(r => ({
              value: r.value,
              label: r.label
            }))}
            placeholder="Select Resolution"
          />

          <div className="date-input-container">
            <label>From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="date-input"
            />
          </div>

          <div className="date-input-container">
            <label>To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              min={fromDate}
              className="date-input"
            />
          </div>

          <div className="date-input-container">
            <label>Position Size</label>
            <input
              type="number"
              value={positionSize}
              onChange={(e) => setPositionSize(Number(e.target.value))}
              className="date-input"
              min={100}
              step={100}
            />
          </div>
        </div>

        <Button
          onClick={handleRunBacktest}
          loading={loading}
          disabled={loading || !selectedStrategy || !selectedInstrument || !selectedResolution}
        >
          Run Backtest
        </Button>
      </div>

      <ResultModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={modalTitle}
        variant={modalVariant}
      >
        {modalContent}
      </ResultModal>
    </div>
  );
};

export default RunBacktest;
